import { cn } from '@/lib/utils'

/**
 * Shared visual language for every control in the filter toolbar.
 *
 * The toolbar is built from two chip systems — the generic FilterButton /
 * MultiSelectFilterButton / OptionsButton inside `StandardFilterBar`, and the
 * per-module clusters (e.g. `ProjectTaskFilterControls`). Both import from here
 * so a chip looks and behaves identically no matter which module renders it.
 *
 * Design: a chip is a dashed-outline pill at rest and fills with the accent
 * tint + solid border (`border-primary/35 bg-primary/10 text-primary`) once it
 * holds a value, with an optional solid count badge for multi-select. Chips
 * stay at h-7 and toolbar gaps at gap-1 so the StandardFilterBar overflow
 * measurement keeps working.
 */

/**
 * Marker class stamped on active chips by `filterChipTriggerClasses`. Carries
 * no styles — StandardFilterBar's overflow engine queries it in the hidden
 * measurement row to know which toolbar items hold a value (including custom
 * elements it can't introspect), so active chips keep their inline spot and
 * empty ones overflow into "More" first.
 */
export const FILTER_CHIP_ACTIVE_MARKER_CLASS = 'filter-chip-active'

/**
 * Trigger button for a filter / options chip.
 * Chips read as dashed-outline pills while empty and fill with the accent
 * tint (solid border) once they hold a value. Pass `border-transparent` via
 * className for ghost triggers like "+ Filter" that shouldn't draw an outline.
 */
export function filterChipTriggerClasses(active: boolean, className?: string) {
  return cn(
    'h-7 flex-shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs transition-colors duration-150',
    active
      ? `${FILTER_CHIP_ACTIVE_MARKER_CLASS} border-primary/35 bg-primary/10 font-semibold text-primary hover:bg-primary/15`
      : 'border-dashed border-border font-medium text-muted-foreground hover:bg-muted/50 hover:text-foreground',
    className,
  )
}

/**
 * Solid accent count badge shown inside an active multi-select chip
 * (e.g. the "2" in an applied "Status" filter).
 */
export const FILTER_CHIP_COUNT_BADGE_CLASSES =
  'inline-flex h-[15px] min-w-[15px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none tabular-nums text-primary-foreground'

/**
 * Field shell for the toolbar search input — slightly taller than the chips
 * (h-8 vs h-7, matching the design's proportions) so it reads as the bar's
 * one text field.
 */
export const FILTER_SEARCH_FIELD_CLASSES =
  'h-8 rounded-lg border border-border/70 bg-muted/40 text-xs transition-colors duration-150 hover:border-border'
